import { createFileRoute } from "@tanstack/react-router";
import { FactStamp } from "@/components/site/FactStamp";
import { lazy, Suspense } from "react";
import { Section, EyebrowHeading } from "@/components/site/Section";
import { Tldr } from "@/components/site/Tldr";
import { ContactForm } from "@/components/site/ContactForm";
import { CallbackBar } from "@/components/site/CallbackBar";
import { ExitIntentOffer } from "@/components/site/ExitIntentOffer";
import { Button } from "@/components/site/Button";
import { PricingIncluded, PricingFAQ } from "@/components/site/PricingExtras";
import { RelatedLinksRail } from "@/components/site/aeo/RelatedLinksRail";
import { QuickAnswer } from "@/components/site/aeo/QuickAnswer";
import { KineticHeading } from "@/components/site/wow/KineticHeading";
import { Reveal } from "@/components/site/wow/Reveal";
import { Magnetic } from "@/components/site/wow/MagneticButton";
import { TrustPromise } from "@/components/site/wow/TrustPromise";
// Tunga interaktiva sektioner code-splitas — de syns långt under fold.
const PricingTierSwitcher = lazy(() => import("@/components/site/wow/PricingTierSwitcher").then(m => ({ default: m.PricingTierSwitcher })));
const CostPerClipBreakdown = lazy(() => import("@/components/site/wow/CostPerClipBreakdown").then(m => ({ default: m.CostPerClipBreakdown })));
import { CompareTable } from "@/components/site/wow/CompareTable";
import { GrainOverlay } from "@/components/site/wow/GrainOverlay";
import { PRODUCT_TERMS, PRICE_ANCHORS, formatPriceSEK } from "@/content/product-terms";

import { featuredCase } from "@/content/cases";
import priserHero from "@/assets/priser-hero.jpg?w=1600&format=avif;webp;jpg&as=picture";
import { Picture } from "@/components/site/Picture";
import { aeoGeo, breadcrumbs, faqPage } from "@/lib/seo/schema";
import { PRICING_FAQ } from "@/components/site/PricingExtras";

export const Route = createFileRoute("/priser")({
  head: () => ({
    meta: [
      { title: "Priser för film — spann och vad som ingår" },
      { name: "description", content: "Transparent prissättning för Pulse och Prime. Fast månadspris eller fast projektpris. Inga timdebiteringar — bara klarhet." },
      { property: "og:title", content: "Priser för film — spann och vad som ingår" },
      { property: "og:description", content: "Transparent prissättning. Inga timdebiteringar." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/priser" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Priser för film — spann och vad som ingår" },
      { name: "twitter:description", content: "Transparent prissättning för Pulse och Prime." },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/priser" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/priser", name: "Priser — Fenny", description: "Transparent prissättning för Pulse och Prime. Fast månadspris eller fast projektpris. Inga timdebiteringar — bara klarhet."})) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbs([{ name: "Hem", path: "/" }, { name: "Priser", path: "/priser" }])) },
      { type: "application/ld+json", children: JSON.stringify(faqPage(PRICING_FAQ)) },
    ],
  }),
  component: PriserPage,
});

function PriserPage() {
  return (
    <div>
      {/* Hero */}
      <section className="relative isolate overflow-hidden border-b border-white/5 pt-44 pb-20 md:pt-56 md:pb-24">
        {/* Cover image — AVIF/WebP via vite-imagetools, JPG fallback */}
        <Picture
          data={priserHero}
          alt="Filmteam från Fenny på inspelning — bild till prissidan för filmproduktion"
          loading="eager"
          fetchPriority="high"
          className="absolute inset-0 -z-20 h-full w-full object-cover object-[72%_center] md:object-[70%_center]"
        />
        {/* Diagonal readability gradient — darkest at the headline */}
        <div
          aria-hidden
          className="absolute inset-0 -z-10"
          style={{
            background:
              "linear-gradient(105deg, color-mix(in oklab, var(--background) 94%, transparent) 0%, color-mix(in oklab, var(--background) 78%, transparent) 45%, color-mix(in oklab, var(--background) 36%, transparent) 80%, transparent 100%)",
          }}
        />
        {/* Bottom fade into next section */}
        <div
          aria-hidden
          className="absolute inset-x-0 bottom-0 -z-10 h-32"
          style={{
            background:
              "linear-gradient(to bottom, transparent 0%, var(--background) 100%)",
          }}
        />
        {/* Subtle butter kiss */}
        <div
          aria-hidden
          className="absolute inset-0 -z-10 opacity-30"
          style={{
            background:
              "radial-gradient(ellipse at 20% 25%, color-mix(in oklab, var(--butter) 18%, transparent) 0%, transparent 55%)",
          }}
        />
        <GrainOverlay opacity={0.05} />
        <div className="container-wide">
          <div className="max-w-4xl">
            <Reveal>
              <div className="mb-6 text-[11px] uppercase tracking-[0.18em] text-butter">Priser</div>
            </Reveal>
            <KineticHeading
              text="Inga timmar. Inga överraskningar. Bara klarhet."
              className="text-5xl font-semibold leading-[1.02] md:text-7xl lg:text-[5.25rem]"
              highlight={{ indices: [5, 6], className: "text-butter" }}
            />
            <Reveal delay={0.4}>
              <p className="mt-8 max-w-2xl text-pretty text-lg text-muted-foreground md:text-xl">
                Tre nivåer för löpande filmflöde. Två alternativa vägar in.
                Allt på bordet — så ni kan välja med hjärnan, inte magen.
              </p>
            </Reveal>
            <Reveal delay={0.55}>
              <QuickAnswer answerId="sv_vad_ingar_i_filmabonnemang" className="mt-8" />
            </Reveal>
          </div>
        </div>
      </section>


      <TrustPromise />

      <PricingIncluded />

      <FactStamp className="mt-8" />

      {/* Tier Switcher */}
      <Section className="pt-4">
        <EyebrowHeading
          eyebrow="Våra abonnemang"
          title="Välj takt. Vi matchar ambitionen."
          lede="Lite för att komma igång, Core för att äga flödet, Pro för att dominera."
          align="center"
        />
        <div className="mx-auto mt-14 max-w-5xl">
          <Suspense fallback={<div style={{ minHeight: 520 }} />}>
            <PricingTierSwitcher />
          </Suspense>
        </div>
        <p className="mx-auto mt-10 max-w-3xl text-center text-xs leading-relaxed text-muted-foreground">
          {PRODUCT_TERMS.pulse.termsSummary} Resa, logi och specialutrustning
          faktureras vid behov — alltid transparent innan ni godkänner.
        </p>
      </Section>

      {/* Cost per clip */}
      <Section className="bg-surface/40">
        <div className="mx-auto max-w-5xl">
          <Suspense fallback={<div style={{ minHeight: 360 }} />}>
            <CostPerClipBreakdown />
          </Suspense>
        </div>
      </Section>

      {/* Alt routes: Pilot + Prime */}
      <Section>
        <EyebrowHeading
          eyebrow="Andra vägar in"
          title="Inte redo för abonnemang? Behöver ni något större?"
          align="center"
        />
        <div className="mt-14 grid gap-6 md:grid-cols-2">
          <article className="flex flex-col rounded-3xl border border-white/10 bg-surface p-8">
            <div className="text-sm font-semibold uppercase tracking-widest text-mint">
              Pilot
              <span className="ml-2 text-xs font-normal normal-case tracking-normal text-muted-foreground">
                · engångsproduktion till fast pris
              </span>
            </div>
            <div className="mt-4 font-serif text-4xl font-semibold leading-tight text-mint">28 995 kr</div>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Produktionsmöten, en hel inspelningsdag, leverans av 2–3 klipp i önskade format.
              Perfekt för att se hur det är att jobba med Fenny innan ni tar steget.{" "}
              <em className="text-foreground/80">Kan endast köpas en gång per kund.</em>
            </p>
            <div className="mt-8 flex-1" />
            <Button to="/kontakt" variant="outline" arrow className="w-full" trackId="book_strategy_meeting" trackProps={{ location: "priser_pilot" }}>
              Starta med Pilot
            </Button>
          </article>

          <article className="flex flex-col rounded-3xl border border-butter/30 bg-surface p-8">
            <div className="text-sm font-semibold uppercase tracking-widest text-butter">
              Prime
              <span className="ml-2 text-xs font-normal normal-case tracking-normal text-muted-foreground">
                · storskalig produktion via offert
              </span>
            </div>
            <div className="mt-4 font-serif text-4xl font-semibold leading-tight text-butter">Skräddarsytt</div>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Reklamfilm, dokumentär, TV-serier, livesändningar och större varumärkesproduktioner.
              Kreativ kraft på högsta nivå.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Typiska budgetar: från 150 000 kr upp till stora kampanjer. Vi offererar efter
              strategimöte där vi förstår omfattning och ambition.
            </p>
            <div className="mt-8 flex-1" />
            <Button to="/prime" variant="outline" arrow className="w-full">Läs mer om Prime</Button>
          </article>
        </div>
      </Section>

      {/* Cinematic quote */}
      <Section className="relative isolate overflow-hidden bg-black">
        <GrainOverlay opacity={0.12} />
        <Reveal>
          <figure className="mx-auto max-w-4xl text-center">
            <div className="text-[11px] uppercase tracking-[0.2em] text-mint/80">Kundröst</div>
            <blockquote className="mt-6 font-serif text-3xl leading-tight text-foreground md:text-5xl">
              &ldquo;{featuredCase.quote?.text}&rdquo;
            </blockquote>
            <figcaption className="mt-8 text-sm uppercase tracking-[0.18em] text-muted-foreground">
              {featuredCase.quote?.name} · {featuredCase.quote?.role}
            </figcaption>
          </figure>
        </Reveal>
      </Section>

      {/* Compare across all tiers */}
      <Section>
        <EyebrowHeading
          eyebrow="Hela menyn"
          title="Pulse vs Prime vs Pilot."
          align="center"
        />
        <div className="mx-auto mt-12 max-w-5xl">
          <CompareTable />
        </div>
      </Section>

      {/* Heritage + Bröllop — privata produktioner */}
      <Section className="bg-surface/40">
        <EyebrowHeading
          eyebrow="Privata produktioner"
          title="Heritage och bröllop"
          lede="Vi filmar inte bara företag. För familjer, par och privatpersoner har vi två dedikerade spår med egna paket och priser."
          align="center"
        />
        <div className="mt-14 grid gap-6 md:grid-cols-2">
          <article className="flex flex-col rounded-3xl border border-white/10 bg-surface p-8">
            <div className="text-sm font-semibold uppercase tracking-widest text-butter">
              Heritage
              <span className="ml-2 text-xs font-normal normal-case tracking-normal text-muted-foreground">
                · livsberättelser & arv
              </span>
            </div>
            <div className="mt-4 font-serif text-4xl font-semibold leading-tight text-butter">Från 39 000 kr</div>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Filmade livsintervjuer, grundarporträtt och dokumentation av tyst kunskap —
              berättelser som annars försvinner. Fasta paket med samtalsledning, inspelning
              och färdig film.
            </p>
            <div className="mt-8 flex-1" />
            <Button to="/heritage" variant="outline" arrow className="w-full">
              Se Heritage-paket
            </Button>
          </article>

          <article className="flex flex-col rounded-3xl border border-white/10 bg-surface p-8">
            <div className="text-sm font-semibold uppercase tracking-widest text-mint">
              Bröllop
              <span className="ml-2 text-xs font-normal normal-case tracking-normal text-muted-foreground">
                · filmer för en av livets största dagar
              </span>
            </div>
            <div className="mt-4 font-serif text-4xl font-semibold leading-tight text-mint">Från 24 995 kr</div>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Tre paket — från highlight-film till full dokumentär med tal, ceremoni och
              fest. Cinematisk look, lugn närvaro på dagen, leverans inom 6 veckor.
            </p>
            <div className="mt-8 flex-1" />
            <Button to="/brollopsfilm" variant="outline" arrow className="w-full">
              Se bröllopspaket
            </Button>
          </article>
        </div>
      </Section>

      <Section className="pt-0">
        <div className="mx-auto max-w-3xl">
          <Tldr>
            <p>
              Fennys priser börjar på <strong>{formatPriceSEK(PRICE_ANCHORS.pulseLite)}/mån</strong> för Pulse Lite (löpande filmflöde), <strong>{formatPriceSEK(PRICE_ANCHORS.pilotFrom)}</strong> för en Pilot-produktion (engångs) och anpassat fastpris för Prime-projekt. Inga timdebiteringar, inga dolda avgifter — fast månads- eller projektpris. {PRODUCT_TERMS.pulse.termsSummary}
            </p>
          </Tldr>
        </div>
      </Section>

      <PricingFAQ />

      <Section className="py-6">
        <RelatedLinksRail sourcePath="/priser" />
      </Section>

      {/* Prisfrågeformulär */}
      <ContactForm
        id="prisforslag"
        location="priser_price_request"
        defaultIntent="annat"
        eyebrow="Fråga om pris"
        title="Få ett prisförslag på er film"
        lede="Beskriv kort vad ni vill göra så återkommer vi med format, upplägg och fast pris — inom en arbetsdag. Ingen registrering, ingen bindning."
      />

      <CallbackBar />

      <ExitIntentOffer location="priser" ctaHref="#prisforslag" />

      {/* Final human CTA */}
      <Section className="pt-0">
        <div className="mx-auto max-w-3xl rounded-3xl border border-white/10 bg-gradient-to-br from-surface via-surface to-butter/[0.05] p-10 text-center md:p-14">
          <div className="text-[11px] uppercase tracking-[0.2em] text-butter">Sista frågan?</div>
          <h2 className="mt-3 text-balance text-3xl font-semibold md:text-4xl">
            Hugg oss på en 15-minuters check.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground">
            Något oklart? Vi reder ut paket, format eller bara om vi är rätt match — utan
            säljpitch. Premium-känsla är att få prata med en människa.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Magnetic>
              <Button to="/kontakt" variant="mint" arrow trackId="book_strategy_meeting" trackProps={{ location: "priser_final_cta" }}>
                Boka 15 min
              </Button>
            </Magnetic>
            <Button href="#prisforslag" variant="outline">Få prisförslag</Button>
          </div>
        </div>
      </Section>
    </div>
  );
}
